import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Hexagon, Circle } from 'lucide-react';
import { Link } from 'react-router-dom';

const slides = [
  {
    id: 1,
    tag: "Premium Signage & Graphics",
    title: "We Build \nImpact.",
    highlight: "Impact.",
    description: "Custom Signage Solutions That Elevate Your Brand Visibility & Drive Real Business Results.",
    imageDesktop: "/images/custom-signs-usa-desktop.jpg",
    imageMobile: "/images/custom-signs-usa-mobile.jpg",
  },
  {
    id: 2,
    tag: "24/7 Brand Visibility",
    title: "Illuminated \nBrilliance.",
    highlight: "Brilliance.",
    description: "Dominate The Road With Bold LED & Neon Signs Designed To Grab Attention Day & Night.",
    imageDesktop: "/images/slide2-desktop.jpg",
    imageMobile: "/images/slide2-mobile.jpg",
  },
  {
    id: 3,
    tag: "Mobile Advertising",
    title: "Dominate \nThe Road.",
    highlight: "The Road.",
    description: "Bold, High-Impact Outdoor Signs That Command Attention & Put Your Brand Ahead of Every Competitor.",
    imageDesktop: "/images/slide3-desktop.jpg",
    imageMobile: "/images/slide3-mobile.jpg",
  }
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "80%"]);
  const shapeUpY = useTransform(scrollYProgress, [0, 1], ["0%", "-200%"]);
  const shapeRotate = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const shapeDownY = useTransform(scrollYProgress, [0, 1], ["0%", "150%"]);

  return (
    <section ref={ref} id="home" className="relative w-full h-[100dvh] flex items-center bg-brand-dark overflow-hidden pt-20">

      {/* 1. The Slider Backgrounds with Ken Burns Effect */}
      {/* REMOVED mode="wait" so images crossfade over each other perfectly */}
      <AnimatePresence>
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1 }}
          animate={{ opacity: 1, scale: 1.1 }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: { duration: 1, ease: "easeInOut" }, // Quick 1-second crossfade
            scale: { duration: 7, ease: "linear" }       // Slow 7-second continuous zoom
          }}
          className="absolute inset-0 z-0"
        >
          {/* Desktop Image */}
          <div
            className="hidden md:block absolute inset-0 bg-cover bg-center bg-no-repeat opacity-80"
            style={{ backgroundImage: `url(${slides[currentSlide].imageDesktop})` }}
          ></div>

          {/* Mobile Image */}
          <div
            className="block md:hidden absolute inset-0 bg-cover bg-center bg-no-repeat opacity-80"
            style={{ backgroundImage: `url(${slides[currentSlide].imageMobile})` }}
          ></div>
        </motion.div>
      </AnimatePresence>

      {/* Lighter Gradient Overlay: Dark at the very top for the navbar, clear in the middle, dark at the bottom for the curve */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/80 via-brand-dark/30 to-brand-dark z-10 pointer-events-none"></div>

      {/* Floating Abstract Shapes */}
      <motion.div style={{ y: shapeUpY, rotate: shapeRotate }} className="absolute top-1/4 left-10 md:left-32 text-brand-cyan/30 z-10 hidden md:block">
        <Hexagon size={140} strokeWidth={0.5} />
      </motion.div>
      <motion.div style={{ y: shapeDownY }} className="absolute bottom-1/3 right-10 md:right-32 text-brand-blue/40 z-10 hidden md:block">
        <Circle size={200} strokeWidth={1} />
      </motion.div>

      {/* 2. Main Text Content */}
      <motion.div
        style={{ y: textY }}
        className="container mx-auto px-6 md:px-12 relative z-20 flex flex-col items-center text-center"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex flex-col items-center"
          >
            <div className="inline-block mb-4 px-4 py-1.5 rounded-full border border-brand-cyan/50 bg-brand-dark/60 backdrop-blur-md text-brand-cyan font-bold tracking-wide text-sm shadow-lg">
              {slides[currentSlide].tag}
            </div>

            {/* Added drop-shadow-2xl to the main text to ensure it stands out against the brighter background */}
            <h1 className="text-6xl md:text-8xl font-heading font-black text-white leading-[1.1] mb-6 uppercase tracking-tighter whitespace-pre-line drop-shadow-[0_5px_15px_rgba(0,0,0,0.5)]">
              {slides[currentSlide].title.split(slides[currentSlide].highlight)[0]}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-cyan to-blue-300 drop-shadow-[0_0_20px_rgba(0,198,255,0.6)]">
                {slides[currentSlide].highlight}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-100 mb-10 max-w-2xl font-medium drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              {slides[currentSlide].description}
            </p>
          </motion.div>
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-6"
        >
          <Link to="/contact" className="flex items-center justify-center gap-3 px-10 py-5 bg-brand-cyan text-brand-dark font-black text-lg rounded-full hover:bg-white hover:scale-105 transition-all duration-300 shadow-[0_0_40px_rgba(0,198,255,0.5)]">
            Start Your Project <ArrowRight size={24} />
          </Link>
        </motion.div>

        {/* 3. Slider Navigation Dots */}
        <div className="flex gap-4 mt-16 z-30 relative">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all duration-500 rounded-full ${currentSlide === index
                  ? 'w-12 h-3 bg-brand-cyan shadow-[0_0_15px_rgba(0,198,255,1)]'
                  : 'w-3 h-3 bg-white/50 hover:bg-white border border-transparent hover:border-brand-cyan'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      </motion.div>

      {/* SVG Arc Divider */}
      <div className="absolute bottom-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none">
        <svg className="relative block w-full h-[100px] md:h-[150px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" className="fill-brand-light"></path>
        </svg>
      </div>
    </section>
  );
};

export default Hero;